import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "teknik_admin_token";

const STATUS_OPTIONS = ["New", "Contacted", "Quoted", "Won", "Lost"];
const STATUS_CLASSES = {
  New: "border-steel/40 bg-steel/15 text-steel",
  Contacted: "border-amber-400/40 bg-amber-500/15 text-amber-300",
  Quoted: "border-blue-400/40 bg-blue-500/15 text-blue-300",
  Won: "border-emerald-400/40 bg-emerald-500/15 text-emerald-300",
  Lost: "border-crimson/40 bg-crimson/15 text-red-300",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-blueprint px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-white/10 bg-deep/80 p-8 backdrop-blur"
      >
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-steel">Teknik Enterprises</div>
        <h1 className="mt-2 font-display text-3xl font-bold uppercase text-white">Admin Login</h1>
        <p className="mt-2 text-sm text-slate-400">Sign in to view website leads.</p>

        <label className="mt-6 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email"
          className="mt-2 w-full rounded-md border border-white/15 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-steel"
          placeholder="admin@teknik.net.in"
        />

        <label className="mt-4 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password"
          className="mt-2 w-full rounded-md border border-white/15 bg-ink px-4 py-2.5 text-sm text-white outline-none focus:border-steel"
          placeholder="••••••••"
        />

        {error ? (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-crimson">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full rounded-full bg-crimson py-3 text-sm font-semibold text-white transition-colors hover:bg-crimson-hover disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <a href="/" className="mt-4 block text-center text-xs text-slate-500 hover:text-steel">
          ← Back to website
        </a>
      </form>
    </div>
  );
}

function InboxView({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState({ status: "New", notes: "" });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const toggleRow = (l) => {
    if (expandedId === l.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(l.id);
    setDraft({ status: l.status || "New", notes: l.notes || "" });
    setSavedId(null);
    setConfirmDeleteId(null);
  };

  const deleteLead = async (l) => {
    setDeletingId(l.id);
    setError("");
    try {
      await axios.delete(`${API}/admin/leads/${l.id}`, authHeader);
      setLeads((prev) => prev.filter((x) => x.id !== l.id));
      setExpandedId(null);
      setConfirmDeleteId(null);
      toast.success(`Lead "${l.full_name}" deleted`);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onLogout();
        return;
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const saveDraft = async (l) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(
        `${API}/admin/leads/${l.id}`,
        { status: draft.status, notes: draft.notes },
        authHeader
      );
      setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, status: data.status, notes: data.notes } : x)));
      setSavedId(l.id);
      setTimeout(() => setSavedId(null), 2500);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        onLogout();
        return;
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(
    async (query) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/admin/leads`, {
          ...authHeader,
          params: { q: query || "" },
        });
        setLeads(data.leads || []);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          onLogout();
          return;
        }
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  useEffect(() => {
    load("");
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/leads/export`, {
        ...authHeader,
        params: { q: q || "" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "teknik-leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setExporting(false);
    }
  };

  const fmtDate = (s) => {
    try {
      return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return s;
    }
  };

  return (
    <div className="min-h-screen bg-blueprint text-slate-200">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-ink/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-5 py-4 sm:px-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel">Teknik Enterprises</div>
            <h1 className="font-display text-2xl font-bold uppercase text-white">Lead Inbox</h1>
          </div>
          <span
            data-testid="admin-lead-count"
            className="rounded-full border border-white/10 px-3 py-1 font-mono text-[11px] text-slate-300"
          >
            {leads.length} leads
          </span>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={exportCsv}
              disabled={exporting || leads.length === 0}
              data-testid="admin-export-btn"
              className="inline-flex items-center gap-2 rounded-full bg-crimson px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50"
            >
              <i className="fa-solid fa-file-csv" />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/40"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="relative max-w-md">
          <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search"
            placeholder="Search by name, company, email, phone, brand…"
            className="w-full rounded-full border border-white/15 bg-ink py-2.5 pl-11 pr-4 text-sm text-white outline-none focus:border-steel"
          />
        </div>

        {error ? (
          <p data-testid="admin-error" className="mt-4 text-sm text-crimson">
            {error}
          </p>
        ) : null}

        <div className="mt-6 overflow-x-auto border border-white/10">
          <table className="w-full min-w-[1040px] border-collapse text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="bg-white/[0.03] text-left font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Application</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500" data-testid="admin-loading">
                    Loading leads…
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500" data-testid="admin-empty">
                    No leads found.
                  </td>
                </tr>
              ) : (
                leads.map((l, i) => {
                  const expanded = expandedId === l.id;
                  const status = l.status || "New";
                  return (
                    <Fragment key={l.id || i}>
                      <tr
                        data-testid={`admin-lead-row-${i}`}
                        onClick={() => toggleRow(l)}
                        className="cursor-pointer border-t border-white/5 align-top hover:bg-white/[0.02]"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-slate-400">{fmtDate(l.created_at)}</td>
                        <td className="px-4 py-3 font-medium text-white">{l.full_name}</td>
                        <td className="px-4 py-3 text-slate-300">{l.company}</td>
                        <td className="px-4 py-3">
                          <a
                            href={`mailto:${l.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-steel hover:underline"
                          >
                            {l.email}
                          </a>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <a
                            href={`tel:${l.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-300 hover:text-steel"
                          >
                            {l.phone}
                          </a>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-slate-300">
                            {l.brand_interest}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            data-testid={`admin-lead-status-${i}`}
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_CLASSES[status] || STATUS_CLASSES.New}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="max-w-xs truncate px-4 py-3 text-slate-300">{l.application_details}</td>
                        <td className="px-4 py-3 text-slate-500">
                          <i className={`fa-solid fa-chevron-down transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </td>
                      </tr>

                      {expanded ? (
                        <tr data-testid={`admin-lead-expanded-${i}`} className="border-t border-white/5 bg-white/[0.03]">
                          <td colSpan={9} className="px-4 py-6" onClick={(e) => e.stopPropagation()}>
                            <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
                              <div>
                                <label className="block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                                  Status
                                </label>
                                <select
                                  value={draft.status}
                                  onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                                  data-testid={`admin-status-select-${i}`}
                                  className="mt-2 w-full rounded-md border border-white/15 bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-steel"
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>

                                <div className="mt-5">
                                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                                    Application
                                  </div>
                                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{l.application_details}</p>
                                </div>

                                {l.ai_analysis ? (
                                  <div className="mt-5">
                                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                                      AI Feasibility Snapshot
                                    </div>
                                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-400">
                                      {l.ai_analysis}
                                    </p>
                                  </div>
                                ) : null}
                              </div>

                              <div className="flex flex-col">
                                <label className="block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                                  Private notes (internal only)
                                </label>
                                <textarea
                                  value={draft.notes}
                                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                                  data-testid={`admin-notes-input-${i}`}
                                  rows={6}
                                  placeholder="Follow-up notes, quotes sent, call summaries…"
                                  className="mt-2 flex-1 resize-y rounded-md border border-white/15 bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-steel"
                                />
                                <div className="mt-4 flex flex-wrap items-center gap-4">
                                  <button
                                    onClick={() => saveDraft(l)}
                                    disabled={saving}
                                    data-testid={`admin-save-lead-${i}`}
                                    className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-hover disabled:opacity-60"
                                  >
                                    <i className="fa-solid fa-floppy-disk" />
                                    {saving ? "Saving…" : "Save changes"}
                                  </button>
                                  {savedId === l.id ? (
                                    <span data-testid="admin-save-flash" className="text-sm font-medium text-emerald-400">
                                      <i className="fa-solid fa-check mr-1" /> Saved
                                    </span>
                                  ) : null}

                                  <div className="ml-auto">
                                    {confirmDeleteId === l.id ? (
                                      <div className="flex items-center gap-2" data-testid={`admin-delete-confirm-${i}`}>
                                        <span className="text-sm text-slate-300">Delete permanently?</span>
                                        <button
                                          onClick={() => deleteLead(l)}
                                          disabled={deletingId === l.id}
                                          data-testid={`admin-delete-yes-${i}`}
                                          className="rounded-full bg-crimson px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-crimson-hover disabled:opacity-60"
                                        >
                                          {deletingId === l.id ? "Deleting…" : "Yes, delete"}
                                        </button>
                                        <button
                                          onClick={() => setConfirmDeleteId(null)}
                                          data-testid={`admin-delete-cancel-${i}`}
                                          className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/40"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => setConfirmDeleteId(l.id)}
                                        data-testid={`admin-delete-lead-${i}`}
                                        className="inline-flex items-center gap-2 rounded-full border border-crimson/40 px-4 py-2 text-sm font-medium text-red-300 transition-colors hover:bg-crimson/15"
                                      >
                                        <i className="fa-solid fa-trash-can" />
                                        Delete lead
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    const verify = async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setChecking(false);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        if (active) {
          setToken("");
          setChecking(false);
        }
      }
    };
    verify();
    return () => {
      active = false;
    };
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (checking) {
    return <div className="grid min-h-screen place-items-center bg-blueprint text-slate-400">Loading…</div>;
  }

  if (!token) {
    return <LoginView onSuccess={(t) => setToken(t)} />;
  }

  return <InboxView token={token} onLogout={logout} />;
}
